/**
 * Checks if the provided value is a function.
 *
 * @remarks
 * Uses `typeof` internally and wraps in `try...catch` to handle edge cases
 * like proxies or throwing accessors.
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the value is a function, otherwise `false`.
 */
export function isFunction<T = any>(value: unknown): value is (...args: any[]) => T {
  try {
    return typeof value === 'function';
  } catch {
    return false;
  }
}

/**
 * Checks if the provided value is strictly the boolean literal `true`.
 *
 * @remarks
 * Safe wrapper using strict identity check with `try...catch`.
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the value is `true`, otherwise `false`.
 */
export function isTrue(value: unknown): value is true {
  try {
    return value === true;
  } catch {
    return false;
  }
}

/**
 * Checks if the provided value is strictly the boolean literal `false`.
 *
 * @remarks
 * Safe wrapper using strict identity check with `try...catch`.
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the value is `false`, otherwise `false`.
 */
export function isFalse(value: unknown): value is false {
  try {
    return value === false;
  } catch {
    return false;
  }
}

/**
 * Checks if the provided value is strictly `null`.
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the value is `null`, otherwise `false`.
 */
export function isNull(value: unknown): value is null {
  return value === null;
}

/**
 * Checks if the provided value is strictly `undefined`.
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the value is `undefined`, otherwise `false`.
 */
export function isUndefined(value: unknown): value is undefined {
  return value === undefined;
}

/**
 * Checks if the provided value is a valid number (excluding NaN).
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the value is a number, otherwise `false`.
 */
export function isNumber(value: unknown): value is number {
  try {
    return typeof value === 'number' && !Number.isNaN(value);
  } catch {
    return false;
  }
}

/**
 * Checks if the provided value is a string.
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the value is a string, otherwise `false`.
 */
export function isString(value: unknown): value is string {
  try {
    return typeof value === 'string';
  } catch {
    return false;
  }
}

/**
 * Checks if the provided value is a boolean.
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the value is a boolean, otherwise `false`.
 */
export function isBoolean(value: unknown): value is boolean {
  try {
    return typeof value === 'boolean';
  } catch {
    return false;
  }
}

/**
 * Checks if the provided value is a plain object (excluding null, arrays, and functions).
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the value is a plain object, otherwise `false`.
 */
export function isObject(value: unknown): value is Record<string, unknown> {
  try {
    return Object.prototype.toString.call(value) === '[object Object]';
  } catch {
    return false;
  }
}

/**
 * Checks if the provided value is an empty plain object.
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the object has no own enumerable keys, otherwise `false`.
 */
export function isEmptyObject(value: unknown): value is Record<string, never> {
  try {
    return isObject(value) && Object.keys(value).length === 0;
  } catch {
    return false;
  }
}

/**
 * Checks if the provided value is an array.
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the value is an array, otherwise `false`.
 */
export function isArray<T = unknown>(value: unknown): value is T[] {
  try {
    return Array.isArray(value);
  } catch {
    return false;
  }
}

/**
 * Checks if the provided value is a Promise-like object.
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the value is thenable and catchable, otherwise `false`.
 */
export function isPromise<T = unknown>(value: unknown): value is Promise<T> {
  try {
    return (
      !!value &&
      typeof (value as any).then === 'function' &&
      typeof (value as any).catch === 'function'
    );
  } catch {
    return false;
  }
}

/**
 * Checks if the provided value is not `null` or `undefined`.
 *
 * @param value - The value to be checked.
 * @returns Returns `true` if the value is neither `null` nor `undefined`.
 */
export function isNonNullable<T>(value: T): value is NonNullable<T> {
  return !isNull(value) && !isUndefined(value);
}
